// src/components/ChatBox/chatTypes.ts
// ★ Shared TypeScript interfaces for the ChatBox component family.
// Updated: adds streaming phase state machine types.

// ─── Chat Phase (state machine) ──────────────────────────────────────
/** The three discrete phases the chat can be in after a user sends a message */
export type ChatPhase = "idle" | "thinking" | "streaming";

// ─── Message ─────────────────────────────────────────────────────────
/** A single chat message */
export interface Message {
    type: "user" | "bot" | "typing" | "image" | "error";
    text: string;
    timestamp: Date;
    /** Optional image preview URL (for uploaded insurance cards) */
    imageUrl?: string;
    /** Whether this bot message is still being streamed */
    isStreaming?: boolean;
    extraInfo?: {
        suggestion?: string[];
        insuranceInfo?: InsuranceInfo;
    };
}

/** Insurance information extracted from a card image */
export interface InsuranceInfo {
    insuranceCompany: string;
    groupName: string;
    groupNumber: string;
    memberId: string;
    planType: string;
    copay?: {
        preventive: string;
        basic: string;
        major: string;
    };
}

/** WebSocket connection status */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** Pending image data for passing from the hero panel to the chatbox */
export interface PendingImageData {
    file: File;
    previewUrl: string;
}

/** Props accepted by the Chatbox component */
export interface ChatboxProps {
    initialMessage?: string;
    pendingMessage?: string;
    pendingImages?: PendingImageData[];
    onMessageProcessed?: () => void;
    startVoiceCall?: boolean;
    onVoiceCallStarted?: () => void;
}

/** Imperative handle exposed by the Chatbox via forwardRef */
export interface ChatboxRef {
    sendMessage: (message: string, images?: PendingImageData[]) => void;
}

// ─── useReducer state & actions ──────────────────────────────────────

export interface ChatState {
    phase: ChatPhase;
    messages: Message[];
    thinkingSteps: string[];
    streamingText: string;
}

export type ChatAction =
    | { type: "ADD_USER_MESSAGE"; text: string }
    | { type: "ADD_TYPING"; text: string; suggestion?: string[] }
    | { type: "THINKING"; content: string }
    | { type: "CHUNK"; content: string }
    | { type: "COMPLETE"; content: string }
    | { type: "ADD_BOT_MESSAGE"; message: Message }
    | { type: "ADD_IMAGE_MESSAGE"; text: string; imageUrl?: string }
    | { type: "REMOVE_TYPING" }
    | { type: "ERROR"; text: string }
    | { type: "SET_MESSAGES"; messages: Message[] }
    | { type: "CLEAR" };

// ─── Chat tips (rotating placeholders) ───────────────────────────────

export const chatTips = [
    'I need to schedule an appointment',
    'What the Cost of root canal?',
    'What are your hours?',
    'What insurances do you take?',
    'What are your payment and financing options?',
];

// ─── Dental health suggestions (shown while typing indicator is active) ──

export const dentalSuggestions = [
    "Brush your teeth at least twice a day to keep your smile bright!",
    "Don't forget to floss daily to remove plaque and food particles!",
    "Use mouthwash for fresh breath and to kill bacteria.",
    "Regular dental checkups help prevent future problems.",
    "Ensure your toothpaste contains fluoride to help protect your enamel.",
    "Drink water after meals to rinse away food particles.",
    "Remember to replace your toothbrush every 3-4 months.",
    "Use a soft-bristled toothbrush to avoid damaging your gums.",
    "An electric toothbrush can help remove more plaque than a manual one.",
    "Ask me about teeth whitening options if you're looking for a brighter smile!",
    "Consider using whitening toothpaste to help brighten your smile over time.",
    "A healthy diet can improve the whiteness of your teeth. Avoid staining foods like coffee and wine.",
    "Chewing sugar-free gum after meals can help freshen your breath.",
    "Limit sugary snacks and drinks to prevent cavities.",
    "Calcium-rich foods like milk, cheese, and leafy greens help strengthen your teeth.",
    "Vitamin C is great for gum health! Try oranges, strawberries, or bell peppers.",
    "Avoid acidic drinks, like soda, to protect your tooth enamel.",
    "If you notice any gum bleeding or swelling, it's time to visit the dentist!",
    "Massaging your gums can help increase blood circulation and keep them healthy.",
    "Gum recession can lead to tooth sensitivity, so be gentle when brushing.",
    "Flossing is essential, but don't forget to floss between your back teeth too!",
    "Consider using interdental brushes for areas that floss may not reach.",
];

// ─── Keyword matching for contextual typing tips ─────────────────────

export const keywordCategories = {
    appointment: ['hour', 'time', 'booking', 'appointment', 'slot', 'reserve', 'schedule'],
    dental: ["Dentistry", 'root canal', 'canal', "Orthodontics", "Periodontics", "Endodontics", "Prosthodontics", "Oral surgery", "Dental implants", "Tooth decay", "Cavities", "Plaque", "Tartar", "Gingivitis", "Periodontal disease", "Tooth extraction", "Root canal", "Dental crown", "Dental bridge", "Dentures", "Braces", "Invisalign", "Retainers", "Teeth whitening", "Veneers", "Bonding", "Enamel", "Dentin", "Pulp", "Gum disease", "Halitosis", "Bad breath", "Fluoride", "Dental sealant", "Tooth sensitivity", "Abscess", "Oral hygiene", "Toothbrush", "Toothpaste", "Dental floss", "Mouthwash", "Dental checkup", "X-rays", "Scaling", "Polishing", "Fillings", "Amalgam", "Composite resin", "Dental anesthesia", "Sedation dentistry", "Cosmetic dentistry", "Smile makeover", "Temporomandibular joint (TMJ)", "Bruxism", "Tooth grinding", "Mouthguard", "Dental emergency", "Chipped tooth", "Knocked-out tooth", "Dental pain", "Orthodontic appliances", "Palate expander", "Wisdom teeth", "Impacted tooth", "Oral cancer", "Canker sores", "Cold sores", "Dental caries", "Malocclusion", "Overbite", "Underbite", "Crossbite", "Open bite", "Dental restoration", "Inlay", "Onlay", "Dental drill", "Ultrasonic scaler", "Dental laser", "Tooth erosion", "Dental abrasion", "Tooth fracture", "Dental bonding", "Pediatric dentistry", "Geriatric dentistry", "Dental phobia", "Saliva", "Xerostomia", "Dry mouth", "Dental trauma", "Tooth discoloration", "Dental aesthetics", "Occlusion", "Bite alignment", "Dental prophylaxis", "Subgingival", "Supragingival", "Dental radiograph", "Panoramic X-ray", "Cephalometric X-ray", "Dental impression", "Digital dentistry"],
    insurance: ['insurance', 'coverage', 'claim', 'benefits', 'eligibility', 'co-payment', 'premium', 'deductibles', 'policies', 'network', 'providers'],
    payment: ['payment', 'billing', 'credit card', 'payment options', 'co-payment', 'payment plans', 'online payments', 'dental financing'],
    pricing: ['pricing', 'cost', 'fees', 'prices', 'price', 'treatment prices', 'consultation fees', 'pricing details', 'price ranges', 'discounts', 'plans', 'estimates'],
};

/** Find the first keyword from a category that matches the user's text */
export const getFirstMatchedKeyword = (text: string, keywords: string[]): string | null => {
    return keywords.find(keyword => text.toLowerCase().includes(keyword.toLowerCase())) || null;
};

/** Generate a contextual typing tip based on the user's message content */
export const getTypingTip = (messageText: string): string => {
    const { appointment, dental, insurance, payment, pricing } = keywordCategories;
    let matched: string | null;

    if ((matched = getFirstMatchedKeyword(messageText, appointment))) {
        return `Tooth Fairy is finding "${matched}" details for you...`;
    } else if ((matched = getFirstMatchedKeyword(messageText, insurance))) {
        return `Tooth Fairy is retrieving details for "${matched}" for you...`;
    } else if ((matched = getFirstMatchedKeyword(messageText, payment))) {
        return `Tooth Fairy is checking payment options for "${matched}"...`;
    } else if ((matched = getFirstMatchedKeyword(messageText, pricing))) {
        return `Tooth Fairy is checking pricing information for "${matched}"...`;
    } else if ((matched = getFirstMatchedKeyword(messageText, dental))) {
        return `Tooth Fairy is reviewing your dental needs regarding "${matched}"...`;
    }

    return "Tooth Fairy is working to fulfill your wish...";
};
